import { ContentTypeName } from "../types";

type CallbackFunctionVariadicAnyReturn = (...args: unknown[]) => unknown;

export const onDocumentReady = function (
  onDocumentReadyCallbackFn: CallbackFunctionVariadicAnyReturn
) {
  if (document.readyState !== "loading") {
    window.setTimeout(onDocumentReadyCallbackFn);
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      onDocumentReadyCallbackFn();
    });
  }
};

export const getWindowHeight = () => {
  return (
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight ||
    0
  );
};

export const getWindowYScroll = () =>
  window.scrollY ||
  document.body.scrollTop ||
  document.documentElement.scrollTop ||
  0;

export const getDocumentHeight = () =>
  Math.max(
    document.body.scrollHeight || 0,
    document.documentElement.scrollHeight || 0,
    document.body.offsetHeight || 0,
    document.documentElement.offsetHeight || 0,
    document.body.clientHeight || 0,
    document.documentElement.clientHeight || 0
  );

export const getCurrentURL = () =>
  `${window.location.origin}${window.location.pathname}`;

export const getTriggersByUrlOnObject = <T>(
  objectWithURLKeys: Record<string, Record<string, T> | undefined> | undefined
) => {
  const currentURL = getCurrentURL();

  const urlEndsWithSlash = currentURL.endsWith("/");

  let foundTriggers = objectWithURLKeys?.[currentURL];

  if (!foundTriggers && urlEndsWithSlash) {
    const urlWithoutSlash = currentURL.substring(0, currentURL.length - 1);
    foundTriggers = objectWithURLKeys?.[urlWithoutSlash];
  }

  return foundTriggers;
};

export const canUseWindowObject = () => typeof window !== "undefined";

export const isSafari = Boolean(
  typeof window.safari === "object" && window.safari.pushNotification
);

export const getDoesZoneHavePreviewZoneAttribute = (element: HTMLElement) => {
  return (
    (element.hasAttribute("preview_zone") ||
      element.hasAttribute("preview_zone_image")) &&
    element.querySelector(".preview-tab")
  );
};

export const setOwnedAndOperatedPreviewDataAttributeAndElement = ({
  element,
  contentTypeName,
  zoneName,
}: {
  element: Element;
  contentTypeName: keyof typeof ContentTypeName;
  zoneName: string;
}) => {
  if (contentTypeName === ContentTypeName.Text) {
    element.setAttribute("preview_zone", "");

    const previewSpanElement = document.createElement("span");
    previewSpanElement.classList.add("preview-tab");
    previewSpanElement.append(zoneName);

    element.prepend(previewSpanElement);
  }

  if (contentTypeName === ContentTypeName.Image) {
    element.setAttribute("preview_zone_image", "");
    {
      const previewSpanElement = document.createElement("span");
      previewSpanElement.classList.add("preview-tab", "preview-tab--img");

      previewSpanElement.append(zoneName);
      element.prepend(previewSpanElement);
    }
  }
};

export const select = (
  selector: string,
  parent: HTMLElement | Document = document
) => parent.querySelector(selector);

export const setEventListener = ({
  selector,
  eventType,
  callback,
}: {
  selector: string;
  eventType: "click" | "time-on-page" | "scroll";
  callback: (event: any) => any;
}) => {
  let element = select(selector);

  if (element) {
    const dataParentElementType = (element as HTMLElement).dataset[
      "parentElement"
    ];

    if (dataParentElementType) {
      const dataParentElement = element.closest(dataParentElementType);

      element = dataParentElement;
    }

    if (element) {
      element.addEventListener(eventType, callback);
    }
  }
};

export const removeEventListener = ({
  selector,
  eventType,
  callback,
}: {
  selector: string;
  eventType: "click" | "time-on-page" | "scroll";
  callback: (event: any) => any;
}) => {
  const element = select(selector);

  if (element) {
    element.removeEventListener(eventType, callback);
  }
};

export interface WaitForItOptions {
  root?: Element;
  timeout?: number;
}

export const useIsomorphicRequestAnimationLayout = (callback: any) => {
  const canUseRequestAnimationFrame =
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function";

  return canUseRequestAnimationFrame
    ? requestAnimationFrame(callback)
    : setTimeout(callback, 1000 / 60);
};

export const waitForElement = async (
  selector: string,
  callBackFn?: (element?: HTMLElement) => void,
  options?: WaitForItOptions
): Promise<HTMLElement> => {
  const wait = async () => {
    const _elementWaiter = new Promise<HTMLElement>((resolve, reject) => {
      let intervalCount = 0;
      let waitForItTimeoutId: number | null = null;
      let observerConnected = false;

      const { root, timeout } = {
        root: options?.root ? options?.root : document.body,
        timeout: options?.timeout,
      };
      const element = root.querySelector(selector);

      if (element) return resolve(element as HTMLElement);

      const observer = new MutationObserver(() => {
        const element = root.querySelector(selector);

        if (element) {
          resolve(root.querySelector(selector)! as HTMLElement);
          observer.disconnect();
        }
      });
      if (timeout && timeout > 0) {
        // @ts-ignore
        waitForItTimeoutId = setInterval(() => {
          intervalCount += 1;

          if (intervalCount === 4) {
            if (observerConnected) {
              observer.disconnect();
              observerConnected = false;
            }

            if (waitForItTimeoutId !== null) {
              clearInterval(waitForItTimeoutId);
            }
          }
          return null;
        }, 650);
      }

      observer.observe(root, {
        childList: true,
        subtree: true,
      });

      observerConnected = true;
    });

    return _elementWaiter;
  };

  const element = await wait();

  if (callBackFn) {
    callBackFn(element);
  }

  return element;
};

export const getElementOffset = (element: HTMLElement) => {
  let _x = 0;
  let _y = 0;

  while (element && !isNaN(element.offsetLeft) && !isNaN(element.offsetTop)) {
    _x += element.offsetLeft - element.scrollLeft;
    _y += element.offsetTop - element.scrollTop;
    // @ts-ignore
    element = element.offsetParent;
  }
  return { top: _y, left: _x };
};

export const addBodyDataAttribute = (name: string, value: string) => {
  document.body.setAttribute(`data-${name}`, value);
};
